import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

const LANG_KEY = 'bookinghub_language';

/**
 * Forces an invite campaign page into the language of its URL.
 *
 * Deliberately NOT LocalizedLanding: that one redirects a visitor whose reader language
 * differs from the URL, so every language is served from one address. An invite link is
 * handed to a specific practice on purpose — if we send a German therapist
 * /de/invite/therapists, they must stay there, whatever their browser says.
 *
 * One URL per language is also what makes the link preview work. A crawler sends no
 * usable Accept-Language, so a single address can only ever carry one card; the card for
 * each URL is written at build time by scripts/prerender-meta.mjs.
 */
export function InviteLanding({ lang, children }: { lang: string; children: ReactNode }) {
  const { i18n } = useTranslation();
  const [ready, setReady] = useState(() => i18n.language === lang);

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      try {
        window.localStorage.setItem(LANG_KEY, lang);
      } catch {
        // Private mode: the URL still decides the language.
      }
    }
    setReady(true);
  }, [i18n, lang]);

  return ready ? <>{children}</> : null;
}
